/**
 * src/lib/missingContacts.ts
 * ------------------------------------------------------------------
 * THE FAMILIES THE SCHOOL CANNOT WRITE TO, SORTED INTO WHO CAN ASK THEM.
 *
 * After the address work of September 2026 the school can reach 672 of its
 * 828 pupils. The remaining 156 are not a bug to fix in code: those families
 * have never given LKC a working address. Nothing in the portal will conjure
 * one, so the only way forward is for somebody who sees the child every day
 * to ask the parent for it.
 *
 * That makes the class teacher the right person, and a per-class list the
 * right shape. This module decides what goes in which list.
 *
 * TWO DECISIONS WORTH KNOWING ABOUT.
 *
 * 1. NO MONEY. The reach report carries what each family owes, because the
 *    bursar needs it. None of it reaches these packs. A class teacher is
 *    being asked to collect an email address, not to know which of their
 *    pupils' families are in arrears, and handing them the figure would make
 *    them a debt collector nobody appointed.
 *
 * 2. NOBODY IS DROPPED. 38 of the 156 are on the alumni roll and 25 are in
 *    Day Care; neither has a class teacher, and four pupils have no class at
 *    all. Silently excluding them would leave the school believing every
 *    family had been asked. They go to the office instead, in a pack of
 *    their own that says why.
 * ------------------------------------------------------------------ */

/** One unreachable pupil, as `checkReminderReach` reports them. */
export interface UnreachableRow {
  studentId: string;
  studentName: string;
  classId?: string;
  className?: string;
  classTeacher?: string;
  admissionNumber?: string;
  guardianNames?: string[];
  phones?: string[];
  /** The address on file that cannot receive mail, if there is one. */
  heldEmail?: string;
}

export interface MissingContactPupil {
  studentId: string;
  name: string;
  admissionNumber: string;
  guardianNames: string[];
  phones: string[];
  heldEmail: string;
  /** False when there is no phone either — the hardest cases, listed first. */
  hasPhone: boolean;
}

export interface MissingContactPack {
  /** Absent for the office pack, which spans several classes. */
  classId?: string;
  className: string;
  teacherName: string;
  /** Why this pack exists at all, printed on it. */
  note?: string;
  pupils: MissingContactPupil[];
  withPhone: number;
  withNothing: number;
}

/** What the office pack is called, and why it is not a class. */
export const OFFICE_PACK_NAME = 'Office — pupils with no class teacher';
export const OFFICE_PACK_NOTE =
  'These pupils are on the alumni roll, in Day Care, or have no class recorded, '
  + 'so there is no class teacher to ask. The office holds them.';

/**
 * ONE NUMBER, WRITTEN TWO WAYS, IS ONE NUMBER.
 *
 * Guardians are recorded with both `phone` and `whatsapp`, and the same
 * handset routinely appears as `76336383` and `+26776336383`. Printing both
 * makes a teacher ring twice and doubts the list.
 *
 * Compared on the last EIGHT digits, which is the Botswana national number -
 * 7xxxxxxx mobile, 2xxxxxxx landline - however it was typed, with or without
 * the 267. The longer spelling is the one kept, because it is the one that
 * dials from anywhere.
 */
export function dedupePhones(phones: readonly (string | undefined)[]): string[] {
  const best = new Map<string, string>();
  for (const raw of phones) {
    const v = String(raw ?? '').trim();
    if (!v) continue;
    const digits = v.replace(/\D/g, '');
    if (!digits) continue;
    const key = digits.slice(-8);
    const seen = best.get(key);
    if (!seen || digits.length > seen.replace(/\D/g, '').length) best.set(key, v);
  }
  return [...best.values()];
}

/** Drop repeats and blanks, keep the order they were recorded in. */
function tidyNames(names: readonly (string | undefined)[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const n of names) {
    const v = String(n ?? '').trim();
    if (!v) continue;
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
}

function toPupil(row: UnreachableRow): MissingContactPupil {
  const phones = dedupePhones(row.phones ?? []);
  return {
    studentId: row.studentId,
    name: row.studentName || row.studentId,
    admissionNumber: row.admissionNumber ?? '',
    guardianNames: tidyNames(row.guardianNames ?? []),
    phones,
    heldEmail: row.heldEmail ?? '',
    hasPhone: phones.length > 0,
  };
}

/**
 * Sort the unreachable into one pack per class teacher, plus the office's.
 *
 * Within a pack the pupils with no telephone number come first: they are the
 * ones nobody can reach by any means, and burying them halfway down an
 * alphabetical list is how they stay unreached. After that, by name.
 */
export function buildMissingContactPacks(rows: readonly UnreachableRow[]): MissingContactPack[] {
  const byClass = new Map<string, MissingContactPack>();
  const office: MissingContactPupil[] = [];

  for (const row of rows) {
    const pupil = toPupil(row);
    const teacher = String(row.classTeacher ?? '').trim();
    const className = String(row.className ?? '').trim();

    // No teacher to ask — whether that is the alumni roll, Day Care, or a
    // pupil with no class recorded at all.
    if (!teacher || !className) { office.push(pupil); continue; }

    const key = row.classId || className;
    let pack = byClass.get(key);
    if (!pack) {
      pack = {
        classId: row.classId,
        className,
        teacherName: teacher,
        pupils: [],
        withPhone: 0,
        withNothing: 0,
      };
      byClass.set(key, pack);
    }
    pack.pupils.push(pupil);
  }

  const order = (a: MissingContactPupil, b: MissingContactPupil) => (
    Number(a.hasPhone) - Number(b.hasPhone) || a.name.localeCompare(b.name)
  );

  const packs = [...byClass.values()];
  for (const p of packs) {
    p.pupils.sort(order);
    p.withPhone = p.pupils.filter((x) => x.hasPhone).length;
    p.withNothing = p.pupils.length - p.withPhone;
  }
  packs.sort((a, b) => a.className.localeCompare(b.className, undefined, { numeric: true }));

  if (office.length) {
    office.sort(order);
    packs.push({
      className: OFFICE_PACK_NAME,
      teacherName: '',
      note: OFFICE_PACK_NOTE,
      pupils: office,
      withPhone: office.filter((x) => x.hasPhone).length,
      withNothing: office.filter((x) => !x.hasPhone).length,
    });
  }

  return packs;
}

/** A filename that survives Windows, a mail client and a teacher's desktop. */
export function packFileName(pack: MissingContactPack): string {
  const safe = pack.className.replace(/[^\w]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
  return `LKC_missing_contacts_${safe || 'class'}.pdf`;
}
